import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { api, tokenStore } from './api';

/**
 * Hell/Dunkel gehört dem **Konto**, nicht dem Gerät.
 *
 * Die Wahl lag früher ausschließlich im Browserspeicher. Die Spalte
 * `users.theme` wurde von `PATCH /api/auth/me` zwar geschrieben und von
 * `/api/auth/me` zurückgeliefert – angewendet hat sie niemand. Wer sich an
 * einem zweiten Gerät anmeldete, fing deshalb wieder bei „System“ an, und die
 * Einstellung wirkte wie vergessen.
 *
 * Jetzt ist die Spalte die Wahrheit. Der Browserspeicher bleibt als
 * Zwischenspeicher davor: Er trägt die Anmeldeseite, auf der es noch kein
 * Konto gibt, und verhindert, dass die Anwendung für einen Bildaufbau hell
 * aufblitzt, bevor `/api/auth/me` geantwortet hat.
 */

export type ThemePreference = 'light' | 'dark' | 'system';
const STORAGE_KEY = 'ff.theme';

const PRAEFERENZEN: ThemePreference[] = ['light', 'dark', 'system'];

function istPraeferenz(value: unknown): value is ThemePreference {
  return typeof value === 'string' && PRAEFERENZEN.includes(value as ThemePreference);
}

interface ThemeContextValue {
  preference: ThemePreference;
  resolved: 'light' | 'dark';
  setPreference: (preference: ThemePreference) => void;
  toggle: () => void;
  /**
   * Wert des angemeldeten Kontos übernehmen.
   *
   * Bewusst getrennt von `setPreference`: Was vom Server kommt, darf nicht
   * wieder zum Server zurückgeschrieben werden – das wäre eine Schleife aus
   * jeder Anmeldung heraus.
   */
  adoptFromAccount: (value: unknown) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

const media = () => window.matchMedia('(prefers-color-scheme: dark)');

function resolve(preference: ThemePreference): 'light' | 'dark' {
  if (preference === 'system') return media().matches ? 'dark' : 'light';
  return preference;
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [preference, setPreferenceState] = useState<ThemePreference>(
    () => (localStorage.getItem(STORAGE_KEY) as ThemePreference) ?? 'system',
  );
  const [resolved, setResolved] = useState<'light' | 'dark'>(() => resolve(preference));

  useEffect(() => {
    const next = resolve(preference);
    setResolved(next);
    document.documentElement.dataset.theme = next;
    localStorage.setItem(STORAGE_KEY, preference);
  }, [preference]);

  // Systemwechsel live übernehmen, solange "System" eingestellt ist.
  useEffect(() => {
    if (preference !== 'system') return;
    const query = media();
    const onChange = () => {
      const next = query.matches ? 'dark' : 'light';
      setResolved(next);
      document.documentElement.dataset.theme = next;
    };
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, [preference]);

  const setPreference = useCallback((next: ThemePreference) => {
    setPreferenceState(next);
    // Nur für ein angemeldetes Konto: Auf der Anmeldeseite gibt es kein Ziel,
    // und ein 401 würde den Umschalter grundlos scheitern lassen. Fehler
    // werden bewusst verschluckt – die Einstellung ist im Browserspeicher
    // bereits gesichert, sie steht also auch dann, wenn der Server gerade
    // nicht erreichbar ist.
    if (tokenStore.access) void api.patch('/api/auth/me', { theme: next }).catch(() => undefined);
  }, []);

  const adoptFromAccount = useCallback((value: unknown) => {
    if (istPraeferenz(value)) setPreferenceState(value);
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({
      preference,
      resolved,
      setPreference,
      toggle: () => setPreference(resolved === 'dark' ? 'light' : 'dark'),
      adoptFromAccount,
    }),
    [preference, resolved, setPreference, adoptFromAccount],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme muss innerhalb von <ThemeProvider> verwendet werden.');
  return context;
}
